import React, { FC, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Icon, TooltipComponent } from "../../../components/Component";
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import {
  Badge,
  Button as AntButton,
  DatePicker,
  Drawer,
  message,
  Progress,
  Select,
  Table,
  Tooltip,
} from "antd";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import IssuesCreation from "./IssuesCreation";
import FormMultiSelect from "../../form-components/MultiSelectComponent";
import { useDispatch } from "react-redux";
import { issueApis } from "../../../api/Issue";
import { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useLocation, useNavigate } from "react-router";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import { tasksApis } from "../../../api/NewTasks";
import { projectsApis } from "../../../api/projects";
import debounce from "lodash.debounce";
import {
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import SearchInput from "../../form-components/SearchComponent";
import { AddAccess } from "../../../components/accessControl/AccessControl";
import ProjectPageShell from "../shared/ProjectPageShell";
import { GenericStatusPill, PriorityPill } from "../shared/statusColors";
const { RangePicker } = DatePicker;

export interface IssueTagsInterface {
  tagId: number;
  tagName: string;
  issueTagId: number;
}
interface Priority {
  priorityName: string;
  priorityId: number;
}

interface ProjectUser {
  userId: string;
  userName: string;
  projectUserId: number;
  roleName: string;
}

export type IssueStatus =
  | "Active"
  | "Inactive"
  | "On Hold"
  | "Closed"
  | "Archived"
  | "Deleted"
  | "In Progress"
  | "Draft"
  | "Pending Approval"
  | "Approval Rejected";

interface FilterOption {
  value: number | string;
  label: string;
}

export type IssueFlagEnum = "Internal" | "External";
export interface IssuesListViewProps {
  issueId: number; // ID
  issueName: string | null; // Issue Name
  projectTitle?: string; // Project
  issueCreatedAt: Date | null; // Created Time
  IssueAssignedToName?: string; // Assignee
  IssueAssignedByName?: string;
  issueTags: IssueTagsInterface[]; // Tags
  updatedAt: Date | null; // Last Modified Time
  deadlineDate: Date | null; // Due Date
  issueStatus: string; // Status
  priorityName?: string; // Severity
  projectId?: number;
}

const IssuesListView: FC<{}> = ({}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );
  const location = useLocation();
  const {
    id: stateProjectId,
    derivedProjectStatus,
    projectName: stateProjectName,
    extra,
    fromDashboard,
  } = location.state || {};

  // Get projectId from Redux layout state as fallback
  const { currentProjectId, currentProjectName } = useSelector(
    (state: RootState) => state.layout
  );

  // Use Redux projectId as fallback when location state doesn't have it
  const projectId = stateProjectId || currentProjectId;
  const projectName = stateProjectName || currentProjectName;

  // Scroll to top when component mounts or when navigating to this page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const columns: ColumnType<IssuesListViewProps>[] = [
    {
      title: "Issue Name",
      dataIndex: "issueName",
      key: "issueName",
      width: 280,
      fixed: "left",
      render(text: any, record: IssuesListViewProps) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`issue-name-${record?.issueId}`}
              onClick={() => {
                navigate(`/pms/project/issue-detail/${record?.issueId}`, {
                  state: {
                    projectId: record?.projectId || projectId,
                    projectName: record?.projectTitle || projectName,
                    derivedProjectStatus,
                    extra,
                  },
                });
              }}
            >
              {record?.issueName || "Demo Issue Name"}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`issue-name-${record?.issueId}`}
              direction="right"
              text={"Click To View and Edit " + record?.issueName + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Reporter",
      dataIndex: "IssueAssignedByName",
      key: "IssueAssignedByName",
      width: 150,
      render: (text: string, record: IssuesListViewProps) => {
        return <>{record?.IssueAssignedByName}</>;
      },
    },
    {
      title: "Created Time",
      dataIndex: "issueCreatedAt",
      key: "issueCreatedAt",
      width: 150,
      render: (text: string, record: IssuesListViewProps) =>
        dayjs(record?.issueCreatedAt).format("DD-MMM-YYYY HH:mm"),
    },
    {
      title: "Assignee",
      dataIndex: "IssueAssignedToName",
      key: "IssueAssignedToName",
      width: 150,
      render: (text: string, record: IssuesListViewProps) => {
        return <>{record?.IssueAssignedToName}</>;
      },
    },
    {
      title: "Tags",
      dataIndex: "issueTags",
      key: "issueTags",
      width: 150,
      render: (
        tags: IssueTagsInterface[],
        record: IssuesListViewProps,
        index: any
      ) => {
        // console.log("index", index);
        const text = record?.issueTags
          ?.map((tag: IssueTagsInterface) => tag.tagName)
          ?.join(", ");

        return (
          <>
            <span>
              {text?.length > 25 ? `${text?.substring(0, 22)}... ` : text}
            </span>
            {text?.length > 25 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`issueTags-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Last Modified Time",
      dataIndex: "UpdatedAt",
      key: "UpdatedAt",
      width: 150,
      render: (text: string, record: IssuesListViewProps) =>
        record?.updatedAt
          ? dayjs(record?.updatedAt).format("DD-MMM-YYYY HH:mm")
          : "N/A", // Placeholder for Last Modified Time, using updatedBy for now
    },
    {
      title: "Due Date",
      dataIndex: "deadlineDate",
      key: "deadlineDate",
      width: 150,
      render: (text: string, record: IssuesListViewProps) =>
        record?.deadlineDate
          ? dayjs(record.deadlineDate).format("DD-MMM-YYYY")
          : "—",
    },
    {
      title: "Severity",
      dataIndex: "priorityName",
      key: "priorityName",
      width: 150,
      render: (text) => (text ? <PriorityPill priority={text} /> : "N/A"),
    },
    {
      title: "Status",
      dataIndex: "issueStatus",
      key: "issueStatus",
      width: 150,
      fixed: "right",
      render: (text: string, record: IssuesListViewProps) => {
        if (!record?.issueStatus) {
          return "--";
        }
        return <GenericStatusPill status={record.issueStatus} />;
      },
    },
  ];
  const methods = useForm({
    mode: "onChange",
  });
  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = methods;
  const [dataSource, setDataSource] = useState<any>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    defaultCurrent: 1,
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "40", "50"],

    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [statusOptions, setStatusOptions] = useState<FilterOption[]>([]);
  const [projectUsers, setProjectUsers] = useState<SelectOption[]>([]);
  const [priorityList, setPriorityList] = useState<SelectOption[]>([]);
  const [projectTagsList, setProjectTagsList] = useState<any[]>([]);
  const [options, setOptions] = useState<
    { label: string; value: string | number }[]
  >([]);
  const [searchQuery, setsearchQuery] = useState(null);

  // State for filters
  const [selectedIssueFilter, setSelectedIssueFilter] = useState<string | null>(
    null
  );

  // dates
  const [creationDateRange, setCreationDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | null
  >(null);
  const [dueDateRange, setDueDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | null
  >(null);
  const [closedDateRange, setClosedDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | null
  >(null);

  const [activeFilters, setActiveFilters] = useState<{
    assignees?: number[];
    statuses?: string[];
    severities?: number[];
    tags?: number[];
    creationDateRange?: [string, string] | null;
    dueDateRange?: [string, string] | null;
    closedDateRange?: [string, string] | null;
  }>({});

  const [filterCount, setFilterCount] = useState(0);
  const [filtersModal, setFiltersModal] = useState(false);

  const [endOfList, setEndOfList] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const [initialLoad, setInitialLoad] = useState(true);

  const fetchStatusOptions = async () => {
    try {
      const response = await issueApis.getStatus();
      if (response && response.data) {
        setStatusOptions(
          response.data.map((status: string) => ({
            label: status,
            value: status,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching status options:", error);
    }
  };

  const fetchProjectUsers = async () => {
    try {
      const response = await projectsApis.fetchProjectUsers(projectId);
      const {
        data: usersList,
        status,
      }: { data: ProjectUser[]; status: boolean } = response || {};
      if (status) {
        const formatted = usersList?.map((user) => ({
          label: user.userName,
          value: Number(user.userId),
          roleName: user.roleName,
        }));
        setProjectUsers(formatted);
      }
    } catch (error) {
      console.error("Error fetching project users:", error);
      messageApi.error("Failed to fetch project users");
    }
  };

  const fetchProjectTags = async () => {
    try {
      const response: any = await projectsApis.fetchProjectTags(projectId);
      const {
        data: tagsList,
        status,
      }: {
        status: boolean;
        data: { tagName: string; tagId: number }[];
      } = response || {};
      if (status) {
        setProjectTagsList(tagsList);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchSeverity = async () => {
    try {
      const response = await tasksApis.priorityList({ listType: "Severity" });
      const { data, status }: { data: Priority[]; status: boolean } =
        response || {};
      if (status) {
        setPriorityList(
          data?.map((item) => ({
            label: item.priorityName,
            value: item.priorityId,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching priority list:", error);
      messageApi.error("Failed to fetch priority list");
    }
  };

  useEffect(() => {
    if (filtersModal && projectId) {
      fetchStatusOptions();
      fetchProjectTags();
      fetchProjectUsers();
      fetchSeverity();
    }
  }, [filtersModal, projectId]);

  const issueFilterOptions = [
    { label: "My reported Issues", value: "my-reported" },
    { label: "Today's Issues", value: "today" },
    { label: "Issues due tomorrow", value: "due-tomorrow" },
    { label: "Issues within next 7 days", value: "next-7-days" },
    { label: "Overdue Issues", value: "overdue" },
    { label: "Today + Overdue Issues", value: "today-and-overdue" },
  ];

  const fetchData = async (params: any, append = false) => {
    setLoading(true);
    try {
      const {
        current,
        pageSize,
        direction,
        sortOrder,
        pageDiff,
        paginationId,
        searchQuery,
        // Filter parameters
        assignees,
        statuses,
        severities,
        tags,
        creationDateRange,
        dueDateRange,
        closedDateRange,
      } = params;

      // Build query parameters
      const queryParams: Record<string, any> = {
        page: current,
        pageSize,
        direction,
        sortOrder,
        pageDiff,
        paginationId: paginationId || undefined,
        projectIds: projectId,
        // Only include myissueFilterName if it's explicitly provided in params
        ...(params.myissueFilterName && {
          myissueFilterName: params.myissueFilterName,
        }),
        searchQuery,
        // issueAssignedByOwnerIds: assignees?.filter(Boolean).join(",") || "",
        // issueStatus: statuses?.filter(Boolean).join(",") || "",
        // priorityIds: severities?.filter(Boolean).join(",") || "",
        // tagIds: tags?.filter(Boolean).join(",") || "",
        // taskIds: tags?.filter(Boolean).join(",") || "",
        // createdStartDate,
        // createdEndDate,
        // dueStartDate,
        // dueEndDate,
        // closedStartDate,
        // closedEndDate,
      };

      // console.log("API params:", queryParams); // In fetchData

      if (params.assignees?.length) {
        queryParams.issueAssignedByOwnerIds = params.assignees
          .filter(Boolean)
          .join(",");
      }

      if (params.statuses?.length) {
        queryParams.issueStatus = params.statuses.filter(Boolean).join(",");
      }

      if (params.severities?.length) {
        queryParams.priorityIds = params.severities.filter(Boolean).join(",");
      }

      if (params.tags?.length) {
        queryParams.tagIds = params.tags.filter(Boolean).join(",");
      }

      // Date range filters
      if (creationDateRange?.[0]) {
        queryParams.issueCreationStartDate = dayjs(creationDateRange[0]).format(
          "YYYY-MM-DD"
        );
      }
      if (creationDateRange?.[1]) {
        queryParams.issueCreationEndDate = dayjs(creationDateRange[1]).format(
          "YYYY-MM-DD"
        );
      }
      if (dueDateRange?.[0]) {
        queryParams.issueDueStartDate = dayjs(dueDateRange[0]).format(
          "YYYY-MM-DD"
        );
      }
      if (dueDateRange?.[1]) {
        queryParams.issueDueEndDate = dayjs(dueDateRange[1]).format(
          "YYYY-MM-DD"
        );
      }
      if (closedDateRange?.[0]) {
        queryParams.issueClosedStartDate = dayjs(closedDateRange[0]).format(
          "YYYY-MM-DD"
        );
      }
      if (closedDateRange?.[1]) {
        queryParams.issueClosedEndDate = dayjs(closedDateRange[1]).format(
          "YYYY-MM-DD"
        );
      }

      // console.log("API Request Params:", queryParams); // Debug log

      const apiResponse = await issueApis.listIssues(queryParams);
      // console.log("API Response:", apiResponse);

      const data = apiResponse?.data || apiResponse?.response?.data;
      if (!data || !data.list) {
        console.warn("No data.list found in response:", data);
        setDataSource([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
        return;
      }

      // Rest of your existing code...
      const firstItemId = data.list[0]?.issueId;
      const lastItemId = data.list[data.list.length - 1]?.issueId;

      const transformedList = data.list.map((item: any) => ({
        ...item,
        issueCreatedAt: item.createdAt ? new Date(item.createdAt) : null,
        issueUpdatedAt: item.updatedAt ? new Date(item.updatedAt) : null,
        IssueAssignedByName: item.IssueAssignedByName || "—",
        IssueAssignedToName: item.IssueAssignedToName || "—",
        _firstPaginationId: firstItemId,
        _lastPaginationId: lastItemId,
      }));

      setPagination((prev) => ({
        ...prev,
        current: parseInt(current, 10),
        pageSize: parseInt(pageSize, 10),
        total: Number(data.count || 0),
      }));

      setDataSource(transformedList);
      setEndOfList(data.list.length < pageSize);
    } catch (error) {
      console.error("Error loading issues:", error);
    } finally {
      setLoading(false);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const { current, pageSize } = newPagination;
    const oldPagination = pagination;

    let direction = "P"; // Default to initial load
    let paginationId = null;
    let pageDiff = 0;

    if (current !== oldPagination.current) {
      if (current > oldPagination.current) {
        // Going forward
        direction = "F";
        paginationId = dataSource[dataSource.length - 1]?._lastPaginationId;
        pageDiff = current - oldPagination.current - 1;
      } else {
        // Going backward
        direction = "B";
        paginationId = dataSource[0]?._firstPaginationId;
        pageDiff = oldPagination.current - current - 1;
      }
    } else if (pageSize !== oldPagination.pageSize) {
      // Handle page size change
      direction = "P";
      pageDiff = 0;
      paginationId = null;
    }

    // Handle sorting
    const sortOrder = sorter.order
      ? sorter.order === "ascend"
        ? "ASC"
        : "DESC"
      : "DESC";

    await fetchData({
      current,
      pageSize,
      direction,
      sortOrder,
      pageDiff,
      paginationId,
      filters,
      searchQuery,
    });
  };

  const refreshListView = (newFilter?: string | null) => {
    try {
      setEndOfList(false);
      setPagination({
        current: 1,
        pageSize: 10,
        defaultCurrent: 1,
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "40", "50"],
        total: 0,
      });
      const filterToUse =
        newFilter !== undefined ? newFilter : selectedIssueFilter;

      refreshData({
        pageDiff: 0,
        ...(filterToUse && { myissueFilterName: filterToUse }),
      }); // calling for initial data load , if the view type is table
    } catch (error) {}
  };

  useEffect(() => {
    if (projectId && initialLoad) {
      refreshListView();
      setInitialLoad(false); // Prevent further initial triggers
    }
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location, initialLoad, projectId]);

  const refreshData = (overRideData?: any) => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
      total: 0,
    }));

    fetchData({
      oldPage: 1,
      current: pagination.current.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: pagination.current - 1,
      paginationId: null,
      dateRange: "Quarter to Date",
      searchQuery,
      // Include active filters
      assignees: activeFilters.assignees,
      statuses: activeFilters.statuses,
      severities: activeFilters.severities,
      tags: activeFilters.tags,
      creationDateRange: activeFilters.creationDateRange,
      dueDateRange: activeFilters.dueDateRange,
      closedDateRange: activeFilters.closedDateRange,
      ...overRideData,
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  // Handle filter submission
  const handleFilterSubmit = (data: any) => {
    // Debug the raw form data
    // console.log("Raw form data:", data);

    // Properly extract the values
    const newFilters = {
      assignees: Array.isArray(data.assigneeFilter)
        ? data.assigneeFilter.filter(Boolean).map((id: any) => Number(id))
        : [],
      statuses: Array.isArray(data.statusFilter)
        ? data.statusFilter
            .filter(Boolean)
            .map((s: { value: any }) => s.value || s)
        : [],
      severities: Array.isArray(data.severityFilter)
        ? data.severityFilter
            .filter(Boolean)
            .map((s: { value: any }) => s.value || s)
        : [],
      tags: Array.isArray(data.tagsFilter)
        ? data.tagsFilter
            .filter(Boolean)
            .map((t: { value: any }) => t.value || t)
        : [],
      creationDateRange:
        data.creationDateRange && data.creationDateRange.length === 2
          ? ([
              dayjs(data.creationDateRange[0]).format("YYYY-MM-DD"),
              dayjs(data.creationDateRange[1]).format("YYYY-MM-DD"),
            ] as [string, string])
          : null,
      dueDateRange:
        data.dueDateRange && data.dueDateRange.length === 2
          ? ([
              dayjs(data.dueDateRange[0]).format("YYYY-MM-DD"),
              dayjs(data.dueDateRange[1]).format("YYYY-MM-DD"),
            ] as [string, string])
          : null,
      closedDateRange:
        data.closedDateRange && data.closedDateRange.length === 2
          ? ([
              dayjs(data.closedDateRange[0]).format("YYYY-MM-DD"),
              dayjs(data.closedDateRange[1]).format("YYYY-MM-DD"),
            ] as [string, string])
          : null,
    };

    // console.log("Processed filters:", newFilters);

    setActiveFilters(newFilters);
    setFilterCount(
      Object.values(newFilters).filter(
        (v) => v && (Array.isArray(v) ? v.length > 0 : true)
      ).length
    );

    // Call fetchData with the new filters
    fetchData({
      current: 1,
      pageSize: pagination.pageSize,
      direction: "P",
      sortOrder: "DESC",
      pageDiff: 0,
      ...newFilters,
    });

    setFiltersModal(false);
  };

  // Reset all filters
  const resetAllFilters = () => {
    methods.reset();
    setActiveFilters({});
    setSelectedIssueFilter(null);
    setFilterCount(0);
    setCreationDateRange(null);
    setDueDateRange(null);
    setClosedDateRange(null);
    refreshListView();

    // Reset form values for date pickers
    methods.setValue("creationDateRange", null);
    methods.setValue("dueDateRange", null);
    methods.setValue("closedDateRange", null);

    // Refresh with default parameters
    fetchData({
      current: 1,
      pageSize: pagination.pageSize,
      direction: "P",
      sortOrder: "DESC",
      pageDiff: 0,
      // No filters
    });
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  const handleBack = () =>
    navigate(`/pms/project/dashboard/${projectId}`, {
      state: {
        ...(location.state || {}),
        id: projectId,
        projectName,
        fromDashboard: undefined,
      },
    });

  const toolbar = (
    <>
      <FormProvider {...methods}>
        <form>
          <div style={{ width: "300px" }}>
            <SearchInput
              name="search"
              placeholder="Search..."
              width="100%"
              prefixIcon={<SearchOutlined />}
              onDebouncedSearch={async (value: any) => {
                setsearchQuery(value);
                setPagination({
                  ...pagination,
                  current: 1,
                });
                await fetchData({
                  oldPage: 1,
                  current: 1,
                  sortOrder: "DESC",
                  pageSize: pagination.pageSize.toString(),
                  direction: "P",
                  pageDiff: 0,
                  paginationId: null,
                  searchQuery: value,
                });
              }}
              debounceDelay={300}
            />
          </div>
        </form>
      </FormProvider>
      <span className="pms-toolbar-spacer" />
      <UncontrolledDropdown>
        <DropdownToggle
          tag="div"
          className="text-primary cursor-pointer d-flex align-items-center w-100"
          style={{ cursor: "pointer" }}
        >
          <span className="me-1">Viewer:</span>
          <span>
            {selectedIssueFilter
              ? issueFilterOptions.find((f) => f.value === selectedIssueFilter)
                  ?.label
              : "All Issues"}
          </span>
          <Icon name="chevron-down" className="ms-1" />
        </DropdownToggle>
        <DropdownMenu style={{ maxHeight: 300, overflowY: "auto" }}>
          <DropdownItem
            tag="div"
            onClick={() => {
              setSelectedIssueFilter(null);
              refreshData();
            }}
            className={!selectedIssueFilter ? "active" : ""}
            style={{ cursor: "pointer" }}
          >
            All Issues
          </DropdownItem>
          {issueFilterOptions.map((filter) => (
            <DropdownItem
              key={filter.value}
              tag="div"
              onClick={() => {
                setSelectedIssueFilter(filter.value);
                refreshData({
                  myissueFilterName: filter.value,
                });
              }}
              className={
                selectedIssueFilter === filter.value ? "active" : ""
              }
              style={{ cursor: "pointer" }}
            >
              {filter.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </UncontrolledDropdown>
      <AddAccess moduleName="Issues">
        <AntButton
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalOpen(true)}
        >
          Add Issue
        </AntButton>
      </AddAccess>
      <AntButton
        type="text"
        icon={<ReloadOutlined />}
        title="Refresh"
        onClick={() => refreshListView()}
      />
      <Badge color="geekblue" size="small" count={filterCount}>
        <AntButton
          type="text"
          icon={<FilterOutlined />}
          onClick={() => setFiltersModal(true)}
        />
      </Badge>
    </>
  );

  return (
    <>
      {contextHolder}
      <ProjectPageShell
        headTitle="Issues"
        breadcrumb={[{ label: projectName }, { label: "Issues" }]}
        onBack={fromDashboard ? handleBack : undefined}
        toolbar={toolbar}
      >
        <SkeletonTable
          loading={loading}
          columns={columns as SkeletonTableColumnsType[]}
        >
          <Table
            rowKey="uid"
            className="pms-hub-table"
            columns={columns}
            dataSource={dataSource}
            pagination={pagination}
            onChange={tableOnChange}
            size="middle"
            scroll={{ x: 1400 }}
          />
        </SkeletonTable>
      </ProjectPageShell>

      <IssuesCreation
        projectId={projectId}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        refreshData={refreshData}
      />
      <Drawer
        maskClosable={false}
        title={
          <div className="d-flex justify-content-between align-items-center">
            <strong>Issue Filters</strong>
            <Button
              onClick={resetAllFilters}
              color="secondary"
              className="m-3 mt-0 mb-0 ps-4 pe-4"
              size="sm"
            >
              <span>Reset All</span>
            </Button>
          </div>
        }
        placement="right"
        width={450}
        onClose={() => setFiltersModal(false)}
        open={filtersModal}
        styles={{ body: { paddingBottom: 80 } }}
      >
        <div className="d-flex align-items-center flex-wrap w-100">
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(handleFilterSubmit)}
              className="w-100"
            >
              {/* Assignee Filter */}
              <div className="mb-2 mt-0">
                <FormMultiSelect
                  name="assigneeFilter"
                  label="Assignee"
                  placeholder="Filter by Assignee"
                  options={projectUsers}
                />
              </div>

              {/* Status Filter */}
              <div className="mb-2 mt-0">
                <FormMultiSelect
                  name="statusFilter"
                  label="Status"
                  placeholder="Filter by Status"
                  options={statusOptions} // or statusFilters if you prefer
                />
              </div>

              {/* Severity Filter */}
              <div className="mb-2 mt-0">
                <FormMultiSelect
                  name="severityFilter"
                  label="Severity"
                  placeholder="Filter by Severity"
                  options={priorityList} // or severityFilters if you prefer
                />
              </div>

              {/* Tags Filter */}
              <div className="mb-2 mt-0">
                <FormMultiSelect
                  name="tagsFilter"
                  label="Tags"
                  placeholder="Filter by Tags"
                  options={projectTagsList.map((tag) => ({
                    value: tag.tagId,
                    label: tag.tagName,
                  }))}
                />
                {/* <label className="form-label">Tags</label>
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Type and press enter to create or select a tag"
                  labelInValue
                  value={tags}
                  onSearch={debouncedFetch}
                  onChange={(value) => {
                    const latest: any = value?.slice(-1)[0]; // Get the last tag

                    if (latest?.label?.length < 2) {
                      // Check if the last tag length is less than 2 characters
                      messageApi.open({
                        type: "error",
                        content: "Enter at least 2 characters",
                        duration: 5,
                      });
                      setOptions([]);
                      return;
                    }
                    setTags(value);
                  }}
                  options={options}
                  tokenSeparators={[","]}
                  tagRender={tagRender}
                  filterOption={false} // Use custom filtering only
                /> */}
              </div>

              {/* Creation Date Range Filter */}
              <div key="creationDateRange" className="mb-2 mt-0">
                <label className="form-label">Creation Date Range</label>
                <RangePicker
                  name="creationDateRange"
                  value={
                    creationDateRange
                      ? [
                          dayjs(creationDateRange[0]),
                          dayjs(creationDateRange[1]),
                        ]
                      : null
                  }
                  onChange={(dates) => {
                    setCreationDateRange(
                      dates as [dayjs.Dayjs, dayjs.Dayjs] | null
                    );
                    methods.setValue("creationDateRange", dates);
                  }}
                  style={{ width: "100%" }}
                />
              </div>

              {/* Due Date Range Filter */}
              <div key="dueDateRange" className="mb-2 mt-0">
                <label className="form-label">Due Date Range</label>
                <RangePicker
                  name="dueDateRange"
                  value={
                    dueDateRange
                      ? [dayjs(dueDateRange[0]), dayjs(dueDateRange[1])]
                      : null
                  }
                  onChange={(dates) => {
                    setDueDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null);
                    methods.setValue("dueDateRange", dates);
                  }}
                  style={{ width: "100%" }}
                />
              </div>

              {/* Closed Date Range Filter */}
              <div key="closedDateRange" className="mb-2 mt-0">
                <label className="form-label">Closed Date Range</label>
                <RangePicker
                  name="closedDateRange"
                  value={
                    closedDateRange
                      ? [dayjs(closedDateRange[0]), dayjs(closedDateRange[1])]
                      : null
                  }
                  onChange={(dates) => {
                    setClosedDateRange(
                      dates as [dayjs.Dayjs, dayjs.Dayjs] | null
                    );
                    methods.setValue("closedDateRange", dates);
                  }}
                  style={{ width: "100%" }}
                />
              </div>

              <div className="actions clearfix">
                <ul>
                  <li>
                    <Button color="primary" size="sm" type="submit">
                      Apply Filters
                    </Button>
                  </li>
                  <li>
                    <Button
                      size="sm"
                      color="primary"
                      className="btn-dim btn btn-secondary"
                      type="button"
                      onClick={() => setFiltersModal(false)}
                    >
                      Cancel
                    </Button>
                  </li>
                </ul>
              </div>
            </form>
          </FormProvider>
        </div>
      </Drawer>
    </>
  );
};

export default IssuesListView;
